import { useMemo } from "react";
import { createTheme } from "@mui/material";
import { useRootFontSizeZoom } from "./useRootFontSizeZoom.js";
/**
 * Returns a copy of `baseTheme` whose breakpoint values are scaled by the
 * current root font-size zoom factor.
 *
 * Pass the returned theme directly into a single MUI `ThemeProvider` so that
 * every `useMediaQuery`, responsive `sx` value, and `theme.breakpoints.up/down()`
 * call in the subtree automatically responds to WCAG 1.4.4 text-size zoom
 * (e.g. the 200% bookmarklet that sets `<html>` `font-size`).
 *
 * ## Why this is better than a nested `ThemeProvider`
 *
 * A nested provider has to observe zoom changes, create a new theme, and
 * re-provide it via React context. That introduces an extra render layer and
 * potential stale-context issues when the outer theme reference is stable.
 * Calling this hook directly in the component that owns the `ThemeProvider`
 * means the scaled theme is derived fresh on every render triggered by a
 * zoom-factor change, with no indirection.
 *
 * ## Scaling direction
 *
 * Breakpoint values are **multiplied** by `zoomFactor`. At 200% text-size
 * zoom (`zoomFactor = 2`) the MUI default `md` breakpoint (900 px) becomes
 * 1800 px — meaning the responsive layout switches at the point where the
 * physical viewport is 1800 px wide, which is what the user perceives as
 * 900 px.
 *
 * ## Usage
 *
 * ```tsx
 * const App = () => {
 *   const theme = useZoomAwareTheme(baseTheme);
 *
 *   return (
 *     <ThemeProvider theme={theme}>
 *       <YourApp />
 *     </ThemeProvider>
 *   );
 * };
 * ```
 *
 * @see useRootFontSizeZoom
 */
export function useZoomAwareTheme(baseTheme) {
    const { zoomFactor } = useRootFontSizeZoom();
    return useMemo(() => {
        if (zoomFactor === 1) {
            return baseTheme;
        }
        const scaledValues = Object.fromEntries(Object.entries(baseTheme.breakpoints.values).map(([key, value]) => [
            key,
            Math.round(value * zoomFactor),
        ]));
        // Spread baseTheme but REPLACE breakpoints entirely with a plain object.
        // deepmerge would preserve the old utility fns (up, down, etc.) from
        // baseTheme.breakpoints, and MUI's createBreakpoints spreads ...other
        // at the end of its return object — overriding the newly generated fns
        // with the stale originals.
        return createTheme({
            ...baseTheme,
            breakpoints: { values: scaledValues },
        });
    }, [baseTheme, zoomFactor]);
}
//# sourceMappingURL=useZoomAwareTheme.js.map